import {
  MerchantSdk,
  PublicSdk,
  getMerchantSdk,
  getPublicSdk,
} from 'projects/content-service-cms/src/app/data/central-asset-platform-sdk/src';
import { AdditionalData, ICentralAssetPlatformService } from '../api';
import {
  ExtendedCollection,
  ICollection,
} from '../../collection-overview/collection.interface';
import { v4 as uuid } from 'uuid';
import { IAuthService } from '../../auth';
import { Injectable } from '@angular/core';
import {
  CreateSettingMutation,
  UpdateCollectionMutation,
} from '../central-asset-platform-sdk/src/merchant-sdk';
import { NgDiscoveryService } from '../../core/services/ng-discovery.service';

@Injectable()
export class CentralAssetPlatformService
  implements ICentralAssetPlatformService
{
  private _merchant_sdk: MerchantSdk;
  private _public_sdk: PublicSdk;

  private _publicPostfix = 'graphql/public/v1';
  private _merchantPostfix = 'graphql/merchant/v1';
  constructor(
    private _authService: IAuthService,
    private discoveryService: NgDiscoveryService,
  ) {}

  private getMerchantSDK = () => {
    if (!this._merchant_sdk) {
      this._merchant_sdk = getMerchantSdk(
        this._authService?.getToken(),
        this.merchantPlatformUrl(),
      );
    }
    return this._merchant_sdk;
  };

  private getPublicSDK = () => {
    if (!this._public_sdk) {
      this._public_sdk = getPublicSdk(
        this._authService?.getToken(),
        this.publicPlatformUrl(),
      );
    }
    return this._public_sdk;
  };

  private publicPlatformUrl = () => {
    return (
      this.discoveryService.config.apiConfiguration.platformApiBaseUrl +
      this._publicPostfix
    );
  };
  private merchantPlatformUrl = () => {
    return (
      this.discoveryService.config.apiConfiguration.platformApiBaseUrl +
      this._merchantPostfix
    );
  };
  updateCollection(
    id: uuid,
    collection: ICollection,
  ): Promise<UpdateCollectionMutation> {
    const r = this.getMerchantSDK().updateCollection({
      settingId: id,
      data: collection,
    });
    return r;
  }

  async createCollection(org: uuid, collection: ICollection): Promise<uuid> {
    const result = (await this.getMerchantSDK().createSetting({
      org: org,
      data: collection,
      type: 'VIRTUAL_STYLING_V1',
    })) as CreateSettingMutation;
    return result.createSetting?.id;
  }

  async getAllCollections(org: uuid): Promise<ExtendedCollection[]> {
    const res = await this.getMerchantSDK().getAllSettings({ org: org });

    //TODO: filtering based on version?
    const collections: ExtendedCollection[] = res.getAllSettings
      .filter((c) => c.type.type === 'VIRTUAL_STYLING_V1')
      .map((c) => {
        return {
          collection: c.data,
          externalReferences: c.externalReferences.map((e) => e.reference),
          id: c.id,
          type: c.type.type,
          schema: c.type.schema,
        };
      });

    return collections;
  }

  public referenceExists = async (org: uuid, reference: string) => {
    const setting = await this.getPublicSDK().findSetting({
      org: org,
      reference: reference,
    });

    return setting.findSetting !== null;
  };

  public addExternalReferenceToSetting = async (
    setting: string,
    reference: string,
  ) => {
    const result = await this.getMerchantSDK().addExternalReferenceToSetting({
      setting: setting,
      reference: reference,
    });
    return result;
  };

  async getThumbnail(org: uuid, ref: string[]): Promise<string[]> {
    const results: Promise<string[]>[] = [];
    for (let i = 0; i < ref.length; i += 100) {
      const block = ref.slice(i, i + 100);
      const promise = this.getMerchantSDK()
        .getThumbnail({
          org: org,
          reference: block,
        })
        .then((result) => {
          if (result.getRequests === null) {
            return [];
          }
          return result.getRequests.map((r) => r.thumbnail);
        });
      results.push(promise);
    }
    return (await Promise.all(results)).flat();
  }

  async getId(org: uuid, ref: string | string[]): Promise<uuid[]> {
    if (Array.isArray(ref)) {
      const results: Promise<uuid[]>[] = [];
      for (let i = 0; i < ref.length; i += 100) {
        const block = ref.slice(i, i + 100);
        const promise = this.getMerchantSDK()
          .getId({
            org: org,
            reference: block,
          })
          .then((result) => {
            if (result.getRequests === null) {
              return [];
            }
            return result.getRequests.map((r) => r.id);
          });
        results.push(promise);
      }
      return (await Promise.all(results)).flat();
    } else {
      const result = await this.getMerchantSDK().getId({
        org: org,
        reference: ref,
      });
      if (result.getRequests === null) {
        return;
      }
      return result.getRequests.map((r) => r.id);
    }
  }
  async getAdditionalData(
    org: uuid,
    ref: string[],
    abortSignal: AbortSignal,
  ): Promise<AdditionalData[]> {
    const results: Promise<AdditionalData[]>[] = [];
    for (let i = 0; i < ref.length; i += 100) {
      const block = ref.slice(i, i + 100);
      const promise = this.getMerchantSDK()
        .getAdditionalData(
          {
            org: org,
            reference: block,
          },
          undefined,
          abortSignal,
        )
        .then((result) => {
          if (result.getRequests === null) {
            return [];
          }
          return result.getRequests.map((r) => {
            return {
              id: r.id,
              thumbnail: r.thumbnail,
              externalReferences: r.externalReferences,
              name: r.name,
            };
          });
        });
      results.push(promise);
    }
    return (await Promise.all(results)).flat();
  }
}
